<script>
import Vue from 'vue';
import {
  mapActions, mapGetters, mapState, mapMutations,
} from 'vuex';
import hexToRgba from 'hex-to-rgba';
import { mixin as clickaway } from 'vue-clickaway';
import VueClipboard from 'vue-clipboard2';

import { UnraidLoading, UnraidLogoMark } from '@unraid/shared-components';
import UnraidSvgUtilLogout from '@unraid/shared-components/assets/svg/util/logout.svg';
import UnraidSvgUtilNewWindow from '@unraid/shared-components/assets/svg/util/new-window.svg';
import UnraidSvgUtilSelect from '@unraid/shared-components/assets/svg/util/select.svg';
import UnraidSvgUtilEdit from '@unraid/shared-components/assets/svg/util/edit.svg';
import UnraidSvgUtilInfo from '@unraid/shared-components/assets/svg/util/info.svg';
import UnraidSvgUiWarning from '@unraid/shared-components/assets/svg/ui/warning.svg';
import UnraidSvgUtilGlobe from '@unraid/shared-components/assets/svg/util/globe.svg';

import KeyAction from '../components/UserProfile/KeyAction.vue';
import MyServers from '../components/UserProfile/MyServers.vue';
import MetaInfo from '../components/UserProfile/MetaInfo.vue';
import UiBeta from '../components/Ui/Beta.vue';

import { SAFE_JSON_PARSE } from '../consts';
import { NODE_ENV } from '../env';
import { log } from '../log';

import i18n from '../i18n';
import store from '../store';

Vue.use(VueClipboard);

export default {
  i18n,
  store,
  name: 'UserProfile',
  mixins: [clickaway],
  components: {
    UnraidLoading,
    UnraidLogoMark,
    KeyAction,
    MetaInfo,
    MyServers,
    UnraidSvgUtilLogout,
    UnraidSvgUtilNewWindow,
    UnraidSvgUtilSelect,
    UnraidSvgUtilEdit,
    UnraidSvgUtilInfo,
    UnraidSvgUiWarning,
    UnraidSvgUtilGlobe,
    UiBeta,
  },
  props: {
    serverstate: String,
    banner: {
      type: String,
      default: '',
    },
    showBannerGradient: {
      type: String,
      default: 'yes',
    },
    bgcolor: [String, Number],
    displaydesc: {
      type: String,
      default: '',
    },
    locale: String,
    localeMessages: String,
    metacolor: String,
    textcolor: String,
    theme: {
      type: String,
      default: 'black',
    }, // black, gray and white, azure

    hideotherservers: { // used for screenshots. If 'true' then other servers will not be shown in UPC dropdown
      type: String,
      default: '',
    },
    /** @REFACTOR props to rely on $serverstate from myservers.php
     * @TODO - refactor these to no longer be props and instead be used in just the Vuex state.
     * This will introduce a breaking chaging to the UPC…because previous versions of the BETA / RCs
     * will not have an updated myservers2.php that has an updated $serverstate array of the values below
    */
    apikey: String, // used for graphql call
    apiVersion: String,
    csrf: String,
    expiretime: [Number, String], // these are run through `parseInt(x, 10)`
    hideMyServers: {
      type: String,
      default: null,
    },
    plgPath: String,
    regWizTime: String, // if this is set we know the server has been registered at some point
    sendCrashInfo: String,
    serverdesc: String,
    servermodel: String,
    serverupdate: Boolean,
    uptime: [Number, String], // these are run through `parseInt(x, 10)`
  },
  data: () => ({
    NODE_ENV,
    avatarFail: false,
    copyingIp: false,
    copyingIpFailed: false,
    dropdownTimeout: '',
    dropdownVisible: false,
    errorStateMessageVisible: true,
    removingPlg: false,
    hoverImage: false,
  }),
  computed: {
    ...mapState([
      'avatar',
      'isLaunchpadOpen',
      'isPromoOpen',
      'isPopUpOpen',
      'guid',
      'guidRegistered',
      'regAtOpen',
      'purchaseWindow',
      'redeemWindow',
      'registered',
      'servername',
      'internalip',
      'state',
      'username',
      'darkTheme',
      'lightTheme',
      'myServersEnv',
      'triggerUPC',
      'graphOnline',
      'myServersError',
      'cloud',
      'plgInstalled',
      'signOutTriggered',
      'pirateKey',
      'deprecatedUnraidSSL',
      /** @REFACTOR props to rely on $serverstate from myservers.php */
      // 'apikey',
      // 'apiVersion',
      // 'csrf',
      // 'expiretime',
      // 'hideMyServers',
      // 'hideotherservers',
      // 'plgPath',
      // 'regWizTime',
      // 'sendCrashInfo',
      // 'serverdesc',
      // 'servermodel',
      // 'serverupdate',
      // 'uptime',

      'accountPayload',
      'accountServerPayload',
      'accountRegisterPayload',
      'accountUnregisterPayload',
      'accountKeyRecoverPayload',
      'accountKeyReplacePayload',
      'accountTrialExtendPayload',
      'accountTroubleshootPayload',
      'processStarted',
      'iframeUrl',
    ]),
    ...mapGetters([
      'errorTooManyDisks',
      'noRemoteApikeyRegisteredWithPlg',
      'signedOutWithKey',
      'stateData',
      'myServersOutOfDate',
      'allowedOrigins',
      'server',
      'plgInstallFailed',
    ]),
    darkMode() {
      return (this.theme === 'black' || this.theme === 'azure');
    },
    usernameButtonText() {
      if ((this.stateData.error && !this.stateData.error.signInToFix) && !this.state === 'EEXPIRED') return this.$t('fixError');
      if (this.registered) return this.username;
      return '';
    },
    usernameButtonTitle() {
      if (this.state === 'ENOKEYFILE') return this.$t('getStarted');
      if (this.state === 'EEXPIRED') return this.$t('upc.trialHasExpiredSeeOptions');
      if (this.stateData.error) return this.$t('learnMore');
      if (this.isPopUpOpen && this.isLaunchpadOpen) return this.$t('upc.confirmClosure');
      if (this.cloud && this.cloud.error) return `${this.$t('upc.myServers.errors.unraidApi.heading')}: ${this.cloud.error}`;
      if (this.myServersError && this.registered) return `${this.$t('upc.myServers.errors.unraidApi.heading')}: ${this.myServersError}`;
      if (this.errorTooManyDisks) return this.$t('upc.errorTooManyDisks.heading');
      if (this.isLaunchpadOpen) return this.$t('closeLaunchpad');
      return this.dropdownVisible ? this.$t('upc.closeDropdown') : this.$t('upc.openDropdown');
    },
    displayKeylineBelowActions() {
      return !!(this.stateData.actions.includes('startTrial')
                  || this.stateData.actions.includes('purchase')
                  || this.stateData.actions.includes('upgrade')
                  || this.stateData.actions.includes('recover')
                  || this.stateData.actions.includes('replace')
                  || this.stateData.actions.includes('extend'));
    },
    extraLinks() {
      return [ // an empty URL will result in a dividing keyline instead of an empty link
        /* eslint-disable */
        ...(!this.hideMyServers && this.registered && !this.plgInstallFailed) ? [ // only show these links when PLG installed and signed in
          {
            url: 'https://connect.myunraid.net',
            text: this.$t('upc.extraLinks.myServers'),
            external: true,
            title: this.$t('upc.extraLinks.newTab', [this.$t('upc.extraLinks.myServers')]),
            emphasize: true,
          },
          // { url: '', title: '' },
          {
            url: `/Settings/ManagementAccess#${this.plgPath ? 'MyServersSettings' : 'UnraidNetSettings'}`,
            text: this.$t('upc.extraLinks.settings.text'),
            settings: true,
            title: this.$t('upc.extraLinks.settings.title'),
          },
          {
            url: 'https://account.unraid.net/',
            text: 'Manage Unraid.net Account',
            external: true,
            title: 'Manage Unraid.net Account',
          },
        ] : [], // NO PLG INSTALLED
        /* eslint-enable */
      ];
    },
    devEnv() { // for non prod UPC display an icon in the UPC dropdown
      const allowedVals = [ // not including 'production' or 'local' here so we don't display the icon
        'staging',
        'development',
      ];
      let item = NODE_ENV; // default
      /** check for the UPC_ENV cookie set via the `upcEnv()` function in the webGUI > dev console */
      const ckName = 'UPC_ENV=';
      const ckArr = document.cookie.split(';');
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < ckArr.length; i++) {
        const tmp = ckArr[i].trim();
        if (tmp.indexOf(ckName) === 0) item = tmp.substring(ckName.length, tmp.length);
      }
      return allowedVals.includes(item) ? `UPC_ENV • ${item}` : null;
    },

    showWarningIcon() {
      return (this.stateData.error
        || this.state === 'EEXPIRED'
        || this.noRemoteApikeyRegisteredWithPlg
        || this.errorTooManyDisks
        || (this.cloud && this.cloud.error)
        || (this.myServersOutOfDate && this.myServersOutOfDate.enabled && this.myServersOutOfDate.error)
        || this.plgInstallFailed
        || this.deprecatedUnraidSSL);
    },

    blur() {
      return (this.isPopUpOpen && !this.isLaunchpadOpen) || this.processStarted;
    },

    processStartedData() {
      if (!this.processStarted) return null;

      switch (this.processStarted) {
      case '/server/key-recover':
        return {
          text: 'Finsh Key Recovery',
          payload: this.accountKeyRecoverPayload,
        };
      case '/server/key-replace':
        return {
          text: 'Finish Key Replacement',
          payload: this.accountKeyReplacePayload,
        };
      case '/server/register':
        return {
          text: 'Finish Registration',
          payload: this.accountRegisterPayload,
        };
      case '/server/unregister':
        return {
          text: 'Finish Unregistration',
          payload: this.accountUnregisterPayload,
        };
      case '/server/extend-trial':
        return {
          text: 'Finish Trial Extension',
          payload: this.accountTrialExtendPayload,
        };
      default:
        return {
          text: 'Finish Process',
          payload: this.accountPayload,
        };
      }
    },
  },
  methods: {
    usernameAvatarButton() {
      if (this.state === 'ENOKEYFILE') return this.toggleLaunchpad();
      this.dropdownVisible = !this.dropdownVisible;
      return this.dropdownVisible;
    },

    ...mapActions([
      'closePopUp',
      'openPopUp',
      'postRobotListeners',
      'receivePostMessage',
      'setStateFromWebComponentProp',
      'startTrial',
      'stopGraphqlClient',
      'togglePromo',
      'updateServerState',
      'validateGuid',
      'reloadWebGUI',
    ]),
    ...mapMutations([
      'SET_IS_LAUNCHPAD_OPEN',
      'SET_IS_PROMO_OPEN',
      'SET_LOCALE',
      /** @REFACTOR props to rely on $serverstate from myservers.php */
      'SET_API_KEY',
      'SET_CSRF',
      'SET_DARK_MODE',
      'SET_EXPIRE_TIME',
      'SET_HIDE_MY_SERVERS',
      'SET_MY_SERVERS_API_VERSION',
      'SET_PLG_PATH',
      'SET_REGWIZ_TIME',
      'SET_SERVERDESC',
      'SET_UPTIME',
      'SET_DEV_MODE',
      'SET_MY_SERVERS_ERROR',
      'SET_MY_SERVERS_ERROR_SHOW_SUPPORT_BTN',
      'SET_SHOW_POST_MESSAGE_STATUS',
    ]),
    closeDropdown() {
      this.dropdownVisible = false;
    },
    dropdownMouseEnter() {
      if (this.state === 'ENOKEYFILE' || this.isLaunchpadOpen) return false;
      clearTimeout(this.dropdownTimeout);
      return true;
    },
    dropdownMouseLeave() {
      this.dropdownTimeout = setTimeout(() => {
        if (this.state === 'ENOKEYFILE') return false;
        this.dropdownVisible = false;
        return true;
      }, 1000);
      return true;
    },
    /**
     * @method: autoTriggerLaunchpad
     * @description: If the user hasn't seen the launchpad in this browser session let's make them see it.
     */
    autoTriggerLaunchpad() {
      setTimeout(() => {
        const mataNe = `${this.servername}_${this.guid ? this.guid.slice(-12) : 'NO_GUID'}`;
        if (!sessionStorage.getItem(mataNe) && this.state === 'ENOKEYFILE') {
          sessionStorage.setItem(mataNe, 'またね'); // "またね" aka "mata ne" aka informal "bye, see you later" in Japanese ;)
          if (!this.isLaunchpadOpen) this.toggleLaunchpad();
        }
      }, 100);
    },
    /**
     * @todo explain yo'self foo
     */
    toggleLaunchpad() {
      // do we need to inject the launchpad before showing it?
      const wcName = 'unraid-launchpad';
      if (!document.getElementsByTagName(wcName).length) {
        const body = document.getElementsByTagName('body')[0];
        const lp = document.createElement(wcName);
        body.appendChild(lp);
      }
      const theWorks = () => {
        if (this.dropdownVisible) this.dropdownVisible = false; // if we open the launchpad from the UPC dropdown, close the dropdown
        if (this.isPopUpOpen) this.closePopUp();
        this.SET_IS_LAUNCHPAD_OPEN(!this.isLaunchpadOpen);
      };
      // since the user is two layers deep, confirm closure
      if (this.isPopUpOpen && this.isLaunchpadOpen) {
        // eslint-disable-next-line no-alert
        if (window.confirm(this.$t('upc.pleaseConfirmClosureYouHaveOpenPopUp'))) theWorks();
      } else {
        theWorks();
      }
    },
    onAvatarError() {
      log.error('Avatar failed to load %s', this.avatar);
      this.avatarFail = true;
    },
    /**
     * @method: initUserProfile
     * @description
     */
    async initUserProfile() {
      log.debug('[initUserProfile]');
      this.setStateFromWebComponentProp(this.serverstate);

      /** @TODO - prop refactor to rely on $serverstate from myservers.php */
      if (this.apikey) this.SET_API_KEY(this.apikey);
      if (this.apiVersion) this.SET_MY_SERVERS_API_VERSION(this.apiVersion);
      if (this.csrf) this.SET_CSRF(this.csrf);
      if (this.expiretime) this.SET_EXPIRE_TIME(this.expiretime);
      if (this.hideMyServers) this.SET_HIDE_MY_SERVERS(this.hideMyServers); /** for support of 6.10 WITHOUT plugin included */
      if (this.plgPath) this.SET_PLG_PATH(this.plgPath); /** for support of 6.10 WITH plugin included */
      if (this.regWizTime) this.SET_REGWIZ_TIME(this.regWizTime);
      if (this.serverdesc) this.SET_SERVERDESC(this.serverdesc);
      if (this.uptime) this.SET_UPTIME(this.uptime);
      if (this.devEnv) this.SET_DEV_MODE(this.devEnv);

      if (this.hideMyServers) { // promo plugin in the console if not installed.
        const msgStyles = [
          'color: white',
          'background: linear-gradient(to right, #E22828 0%, #FF8C2F 100%)',
          'font-size: 10px',
          'padding: 4px',
        ].join(';');
        log.log(`%c${this.$t('upc.enhanceYourExperienceWithMyServers')} • https://docs.unraid.net/category/unraid-connect`, msgStyles);
      }
      if (this.localeMessages) {
        log.debug('[this.locale] %o', this.locale);
        const [err, upcMsgs] = SAFE_JSON_PARSE(decodeURIComponent(this.localeMessages));
        if (err) log.error('[initUserProfile.localeMessages] %s', 'Unable to set locale and locale messages');
        this.$i18n.locale = this.locale;
        this.$i18n.setLocaleMessage(this.locale, upcMsgs[this.locale]);
        log.debug(`[initUserProfile.$i18n] ${this.$i18n.locale} locale & messages set %o`, upcMsgs[this.locale]);
        this.SET_LOCALE(this.locale);
      }
      if (this.signedOutWithKey && this.regWizTime) {
        log.debug('[initUserProfile] %o', { signedOutWithKey: this.signedOutWithKey, regWizTime: this.regWizTime });
        await this.validateGuid();
      } else this.autoTriggerLaunchpad();
      // install plg via promo component. now on refresh trigger launchpad
      if (sessionStorage.getItem('clickedInstallMyServers')) {
        log.debug('[initUserProfile] sessionStorage clickedInstallMyServers');
        this.toggleLaunchpad();
      }
      if (sessionStorage.getItem('showPromoPostTrialInstall')) {
        log.debug('[initUserProfile] sessionStorage showPromoPostTrialInstall');
        this.togglePromo();
      }
      // here to listen for postMessages from pop-up window
      window.addEventListener('message', (event) => {
        this.receivePostMessage({ inject: false, event });
      }, false);
      this.postRobotListeners();
    },
    /**
     * Click server name to copy LAN IP with error handlers below to allow user to manually copy
     */
    copyIpSuccess() {
      log.debug('[copyIpSuccess] %s', this.internalip);
      this.copyingIp = !this.copyingIp;
      setTimeout(() => { this.copyingIp = !this.copyingIp; }, 750);
    },
    copyIpError() {
      this.copyingIpFailed = true;
      clearTimeout(this.copyingFailedTimeout);
      this.copyingFailedTimeout = setTimeout(() => { this.copyingIpFailed = !this.copyingIpFailed; }, 2000);
      return log.error('Unable to copy IP: %s', this.internalip);
    },
    hoverCopyFailed() { // prevent closure when hovering the failed message
      log.debug('[hoverCopyFailed] %s', this.internalip);
      clearTimeout(this.copyingFailedTimeout);
    },
    leaveCopyFailed() { // hide failed message on a timer
      log.debug('[leaveCopyFailed] %s', this.internalip);
      clearTimeout(this.copyingFailedTimeout);
      this.copyingFailedTimeout = setTimeout(() => { this.copyingIpFailed = !this.copyingIpFailed; }, 1500);
    },
    copyFailedClickaway() { // hide failed messasge when clicking away
      log.debug('[copyFailedClickaway] %s', this.internalip);
      clearTimeout(this.copyingFailedTimeout);
      this.copyingIpFailed = false;
    },
  },
  watch: {
    signedOutWithKey(newVal, oldVal) { // check if we need to trigger this after changing
      log.debug('[signedOutWithKey]', { newVal, oldVal, regWizTime: this.regWizTime });
      if (newVal && !oldVal && this.regWizTime) this.validateGuid();
    },
    /**
     * auto close launchpad after sign in and closure of pop-up
     */
    isPopUpOpen(newVal, oldVal) {
      if (!this.isLaunchpadOpen) return;
      if ((!newVal && oldVal) && (!this.regAtOpen && this.registered)) { // if pop up is closing && we weren't registered but now are
        this.SET_IS_LAUNCHPAD_OPEN(false);
      }
    },
    /**
     * remote trigger of UPC dropdown
     */
    triggerUPC(newVal) {
      if (newVal) { // only when triggering, not when reseting value
        setTimeout(() => { this.dropdownVisible = true; }, 150);
      }
    },
    /**
     * watch change of graphOnline = false to graphOnline = true
     * starts subscription for graph replacement of state.php
     */
    graphOnline(n, o) {
      if (n && !o) this.updateServerState();
    },
    /** If the promo opens, close the dropdown */
    isPromoOpen(n, o) {
      if (n && !o) this.dropdownVisible = false;
    },
    signOutTriggered(newVal, oldVal) {
      if (newVal && this.graphOnline) { // sign out with plugin
        log.debug('[watch.signOutTriggered] disconnect from unraid-api', { newVal, oldVal, apikey: this.apikey });
        this.stopGraphqlClient(this.apikey);
        if (this.myServersError) { // clear out since we're going offline
          this.SET_MY_SERVERS_ERROR('');
          this.SET_MY_SERVERS_ERROR_SHOW_SUPPORT_BTN(false);
        }
      }
    },
  },
  created() {
    this.initUserProfile();
  },
  beforeMount() {
    const body = document.getElementsByTagName('body')[0];
    const defaultColors = {
      darkTheme: {
        alpha: '#1c1b1b',
        beta: '#f2f2f2',
        gamma: '#999999',
      },
      lightTheme: {
        alpha: '#f2f2f2',
        beta: '#1c1b1b',
        gamma: '#999999',
      },
    };
    let { alpha, beta, gamma } = this.darkMode ? defaultColors.darkTheme : defaultColors.lightTheme;
    // overwrite with hex colors set in webGUI @ /Settings/DisplaySettings
    if (this.textcolor) alpha = this.textcolor;
    if (this.bgcolor) {
      beta = this.bgcolor;
      body.style.setProperty('--color-customgradient-start', hexToRgba(beta, 0));
      body.style.setProperty('--color-customgradient-end', hexToRgba(beta, 0.9));
    }
    if (this.metacolor) gamma = this.metacolor;
    body.style.setProperty('--color-alpha', alpha);
    body.style.setProperty('--color-beta', beta);
    body.style.setProperty('--color-gamma', gamma);
    // box shadow
    body.style.setProperty('--shadow-beta', `0 25px 50px -12px ${hexToRgba(beta, 0.15)}`);
    body.style.setProperty('--ring-offset-shadow', `0 0 ${beta}`);
    body.style.setProperty('--ring-shadow', `0 0 ${beta}`);
  },
};
</script>

<template>
  <div
    id="UnraidUPC"
    class="UnraidUPC"
    :class="{
      '--hasBanner-default': banner && showBannerGradient !== 'no' && !bgcolor,
      '--hasBanner-custom': banner && showBannerGradient !== 'no' && bgcolor,
    }">
    <div v-if="blur" class="absolute z-10 inset-0 flex flex-col items-end justify-center pr-6">
      <div class="text-center inline-flex flex-col items-center justify-center">
        <template v-if="isPopUpOpen && !isLaunchpadOpen">
          <button @click="openPopUp()" class="text-14px button button-alt">{{ $t('backToPopUp') }}</button>
          <button @click="closePopUp()" class="hover:underline text-12px mt-4px">{{ $t('closePopUp') }}</button>
        </template>
        <template v-if="!isPopUpOpen && processStarted">
          <button @click="openPopUp(processStartedData.payload)" class="text-14px button button-alt">{{ processStartedData.text }}</button>
          <button @click="reloadWebGUI" class="hover:underline text-12px mt-4px">{{ 'Reload to Cancel' }}</button>
        </template>
      </div>
    </div>

    <MetaInfo
      @purchase="openPopUp(purchaseWindow)"
      @upgrade="openPopUp(purchaseWindow)"
      :class="{
        'blur opacity-30 pointer-events-none transition-all duration-150 ease-in-out': blur,
      }" />

    <div
      class="relative z-0 flex flex-row items-center justify-end h-full"
      :class="{
        'blur opacity-30 pointer-events-none transition-all duration-150 ease-in-out': blur,
    }">
      <!-- extra border classes here to help center the text with the button, since the button text has a border on it.
      without these classes even with items-center it's not actually centered due to the border space -->
      <h1 class="relative text-18px border-t-0 border-r-0 border-l-0 border-b-2 border-transparent">
        <template v-if="displaydesc">
          <span :title="servermodel">{{ serverdesc }}</span>
          <span class="text-grey-mid px-8px">&bull;</span>
        </template>
        <span
          v-clipboard:copy="internalip"
          v-clipboard:success="copyIpSuccess"
          v-clipboard:error="copyIpError"
          :title="$t('lanIp', [internalip])"
          class="cursor-pointer"
        >{{ servername }}</span>
        <transition name="fade" mode="out-in">
          <span v-if="copyingIp" class="text-12px absolute right-0 bg-gradient-to-r from-red to-orange text-center block w-100px rounded">{{ $t('upc.lanIpCopied') }}</span>
          <span v-if="copyingIpFailed"
            class="text-12px font-semibold px-4px absolute right-0 bg-gradient-to-r from-red to-orange text-center block rounded"
            @mouseenter="hoverCopyFailed"
            @mouseleave="leaveCopyFailed"
            v-on-clickaway="copyFailedClickaway"
          >LAN IP: <span class="select-all">{{ internalip }}</span></span>
        </transition>
      </h1>

      <div class="block w-2px h-24px ml-16px bg-grey-mid"></div>

      <div
        v-on-clickaway="closeDropdown"
        @mouseenter="dropdownMouseEnter()"
        @mouseleave="dropdownMouseLeave()"
        class="relative flex items-center justify-end h-full"
      >
        <button
          @click="usernameAvatarButton()"
          :class="{
            'text-orange-dark': isLaunchpadOpen || (!username && !stateData.error),
            'text-alpha': username && !isLaunchpadOpen,
            'text-red': stateData.error,
          }"
          class="UnraidUPC-username group text-18px hover:text-alpha focus:text-alpha border border-transparent flex flex-row justify-end items-center h-full px-16px outline-none focus:outline-none"
          :title="usernameButtonTitle"
        >
          <!-- show info icon for non-error of myServersOutOfDate b/c it still allows the API to connect -->
          <UnraidSvgUtilInfo v-if="myServersOutOfDate && myServersOutOfDate.enabled && !myServersOutOfDate.error" class="text-red fill-current relative w-14px h-14px mr-8px" />
          <UnraidSvgUiWarning v-else-if="showWarningIcon" class="text-red fill-current relative w-24px h-24px mr-8px" />
          <!-- username + down arrow when signed in -->
          <span v-if="usernameButtonText" class="inline-flex items-center bg-transparent border-t-0 border-l-0 border-r-0 border-b-2 border-transparent group-hover:border-orange-dark group-focus:border-orange-dark transition-colors duration-150 ease-in-out">
            {{ usernameButtonText }}
            <UnraidSvgUtilSelect v-if="registered && !isLaunchpadOpen" class="fill-current w-8px h-8px ml-8px" />
          </span>
          <!-- Signed In Avatar -->
          <span
            @mouseenter="hoverImage = true"
            @mouseleave="hoverImage = false"
            class="relative z-0 flex items-center justify-center w-36px h-36px rounded-full"
            :class="{
              'ml-8px': usernameButtonText,
              'bg-transparent': registered && !avatarFail,
              'bg-gradient-to-r from-red to-orange': !registered || avatarFail,
            }"
          >
            <img
              v-if="registered && !avatarFail"
              @error="onAvatarError"
              :src="avatar"
              :alt="`${$t('upc.avatarAlt', [username])}`"
              class="absolute z-10 inset-0 w-36px h-36px rounded-full overflow-hidden">
            <template v-else>
              <UnraidLogoMark v-if="!hoverImage" :title="usernameButtonTitle" gradient-start="#fff" gradient-stop="#fff" class="relative z-0 w-28px" />
              <UnraidLoading v-else :title="usernameButtonTitle" gradient-start="#fff" gradient-stop="#fff" class="relative z-0 w-28px" :height="28" />
            </template>
          </span>
          <!-- down arrow when not signed in -->
          <UnraidSvgUtilSelect v-if="!usernameButtonText" class="fill-current w-8px h-8px ml-8px" />
        </button>

        <transition name="slideVerticalFade" mode="out-in">
          <div
            v-show="dropdownVisible"
            class="UnraidUPC-dropdown"
            :class="{
              'border-grey-darkest': darkMode,
              'border-grey-light': !darkMode,
            }"
          >
            <header v-if="!hideMyServers" class="text-14px flex flex-row items-start justify-between pt-16px px-16px rounded-t">
              <h5 class="text-18px leading-none inline-flex flex-row items-center">
                <span class="font-semibold">Connect</span>
                <ui-beta class="ml-6px" />
                <span v-if="myServersEnv" class="ml-8px" :title="`API • ${myServersEnv}`">⚙️</span>
                <span v-if="devEnv" class="ml-8px" :title="devEnv">⚠️</span>
              </h5>
            </header>
            <ul class="list-reset flex flex-col gap-y-4px m-8px p-0">
              <template v-if="NODE_ENV === 'development'">
                <li>
                  <KeyAction @click="toggleLaunchpad()" text="[DEV] Launchpad" :external="false" />
                </li>
                <li>
                  <KeyAction @click="openPopUp(accountServerPayload)" text="[DEV] Pop-up" />
                </li>
                <li class="my-8px mx-12px">
                  <hr class="w-full h-2px bg-gradient-to-r from-red to-orange shadow-none border-none rounded-none" />
                </li>
              </template>

              <template v-if="!hideMyServers && !registered">
                <li>
                  <button
                    v-if="!iframeUrl"
                    @click="openPopUp(accountRegisterPayload)"
                    class="group flex flex-row items-center justify-between"
                    :title="$t('upc.opensNewHttpsWindow.base', ['Sign In with Unraid.net Account'])"
                  >
                    <span class="inline-flex flex-row items-center">
                      <UnraidSvgUtilGlobe class="fill-current w-12px h-12px mr-8px" />
                      {{ 'Sign In with Unraid.net Account' }}
                    </span>
                    <UnraidSvgUtilNewWindow class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
                  </button>
                  <a
                    v-else
                    :href="iframeUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="group flex flex-row items-center justify-between"
                    title="Open server webGUI in new tab to Sign In"
                  >
                    <span class="inline-flex flex-row items-center">
                      <UnraidSvgUtilLogout class="fill-current w-12px h-12px mr-8px" />
                      {{ 'Sign In (new tab)' }}
                    </span>
                    <UnraidSvgUtilNewWindow class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
                  </a>
                </li>
              </template>

              <template v-if="displayKeylineBelowActions">
                <KeyAction v-if="stateData.actions.includes('startTrial')"
                  @click="startTrial()"
                  :text="$t('actions.startTrial')" />
                <template v-if="stateData.actions.includes('purchase')">
                  <KeyAction
                    @click="openPopUp(purchaseWindow)"
                    :no-iframe-allowed="true"
                    :text="$t('actions.purchase')" />
                  <KeyAction
                    @click="openPopUp(redeemWindow)"
                    :no-iframe-allowed="true"
                    :text="$t('actions.redeemActivationCode')" />
                </template>
                <KeyAction v-if="stateData.actions.includes('upgrade')"
                  @click="openPopUp(purchaseWindow)"
                  :no-iframe-allowed="true"
                  :text="$t('actions.upgrade')" />
                <KeyAction v-if="stateData.actions.includes('recover')"
                  @click="openPopUp(accountKeyRecoverPayload)"
                  :no-iframe-allowed="true"
                  :text="$t('actions.recover')" />
                <KeyAction v-if="stateData.actions.includes('replace')"
                  @click="openPopUp(accountKeyReplacePayload)"
                  :no-iframe-allowed="true"
                  :text="$t('actions.replace')" />
                <KeyAction v-if="stateData.actions.includes('extend')"
                  @click="openPopUp(accountTrialExtendPayload)"
                  :no-iframe-allowed="true"
                  :text="$t('actions.extend')" />
                <li class="my-8px mx-12px">
                  <hr class="w-full h-2px bg-gradient-to-r from-red to-orange shadow-none border-none rounded-none" />
                </li>
              </template>

              <template v-if="((!hideMyServers && registered) || pirateKey || stateData.error.message || errorTooManyDisks || myServersOutOfDate.enabled || plgInstallFailed || deprecatedUnraidSSL)">
                <MyServers
                  :hideMyServers="hideMyServers"
                  :errorStateMessageVisible="errorStateMessageVisible"
                  :hideotherservers="hideotherservers"
                  @contact-support="openPopUp(accountTroubleshootPayload)"
                  @toggle-error="errorStateMessageVisible = !errorStateMessageVisible" />
              </template>

              <template v-for="(link, index) in extraLinks">
                <li v-if="link.url" :key="index">
                  <a
                    :href="link.url"
                    :target="`${link.external ? '_blank' : ''}`"
                    :rel="`${link.external ? 'noopener noreferrer' : ''}`"
                    :title="link.title"
                    class="flex flex-row items-center"
                    :class="{
                      'btn-emphasize': link.emphasize
                    }"
                  >
                    <UnraidSvgUtilNewWindow v-if="link.external" class="flex-shrink-0 fill-current w-12px h-12px mr-8px" />
                    <UnraidSvgUtilEdit v-if="link.settings" class="flex-shrink-0 fill-current w-12px h-12px mr-8px" />
                    <UnraidSvgUtilInfo v-if="link.info" class="flex-shrink-0 fill-current w-12px h-12px mr-8px" />
                    {{ link.text }}
                  </a>
                </li>
                <div v-else :key="`${index}_else`" class="mx-12px">
                  <hr class="w-full h-2px my-8px bg-gradient-to-r from-red to-orange shadow-none border-none rounded-none" />
                </div>
              </template>

              <!-- plg NOT installed -->
              <li v-if="hideMyServers">
                <button
                  @click="togglePromo()"
                  class="group flex flex-row items-center justify-between"
                  :title="`Enhance your experience with Connect`"
                >
                  <span class="inline-flex flex-row items-center">
                    <UnraidSvgUtilInfo class="flex-shrink-0 fill-current w-12px h-12px mr-8px" />
                    <span>
                      {{ `Enhance your experience with Connect` }}
                      <ui-beta class="inline-block transform scale-90 opacity-70" colorClasses="text-beta border-beta group-hover:text-white group-focus:text-white group-hover:border-white group-focus:border-white" />
                    </span>
                  </span>
                </button>
              </li>
              <!-- plg installed -->
              <template v-else>
                <li v-if="registered && !plgInstallFailed">
                  <button
                    v-if="!iframeUrl"
                    @click="openPopUp(accountUnregisterPayload)"
                    class="group flex flex-row items-center justify-between"
                    :title="$t('upc.opensNewHttpsWindow.base', [$t('actions.signOutUnraidNet')])"
                  >
                    <span class="inline-flex flex-row items-center">
                      <UnraidSvgUtilLogout class="fill-current w-12px h-12px mr-8px" />
                      {{ $t('actions.signOutUnraidNet') }}
                    </span>
                    <UnraidSvgUtilNewWindow class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
                  </button>
                  <a
                    v-else
                    :href="iframeUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="group flex flex-row items-center justify-between"
                    title="Open server webGUI in new tab to Sign Out"
                  >
                    <span class="inline-flex flex-row items-center">
                      <UnraidSvgUtilLogout class="fill-current w-12px h-12px mr-8px" />
                      {{ 'Sign Out (new tab)' }}
                    </span>
                    <UnraidSvgUtilNewWindow class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
                  </a>
                </li>
              </template>

            </ul>
          </div>
        </transition>

      </div>
    </div>
  </div>
</template>

<style lang="postcss">
@tailwind base;
@tailwind components;
@tailwind utilities;

body {
  --color-alpha: #1c1b1b;
  --color-beta: #f2f2f2;
  --color-customgradient-start: rgba(242, 242, 242, .0);
  --color-customgradient-end: rgba(242, 242, 242, .85);
}

hr {
  @apply w-full shadow-none border-none rounded-none;
}

.UnraidUPC {
  @apply text-alpha;
  @apply relative z-20 flex flex-col h-full pl-80px rounded;

  color: var(--color-alpha);

  .SelectContainer::after {
    right: 0;
  }
}

.--hasBanner-default {
  background-image: linear-gradient(90deg, rgba(0, 0, 0, 0) 0, var(--color-beta) 12%);
}

.--hasBanner-custom {
  background-image: linear-gradient(90deg, var(--color-customgradient-start) 0, var(--color-customgradient-end) 12%);
}

.UnraidUPC-dropdown {
  @apply text-beta absolute z-30 right-0 mr-8px bg-alpha border rounded-lg min-w-310px max-w-350px;

  top: 95%;
  box-shadow: var(--ring-offset-shadow), var(--ring-shadow), var(--shadow-beta);

  &::before {
    @apply absolute z-20 block;

    content: '';
    width: 0;
    height: 0;
    top: -10px;
    right: 32px;
    border-right: 11px solid transparent;
    border-bottom: 11px solid var(--color-alpha);
    border-left: 11px solid transparent;
  }

  button,
  a:not(.button-pill) {
    @apply text-left text-14px text-beta w-full px-16px py-8px cursor-pointer rounded-md;
  }

  .link-error {
    @apply text-red;
  }

  button:hover,
  button:focus,
  a:not(.button-pill):hover,
  a:not(.button-pill):focus {
    @apply text-white;
    background-image: linear-gradient(to right, rgba(226, 40, 40, 0.6), rgba(255, 140, 47, 0.6));
    outline: 0;
  }

  a.btn-emphasize,
  button.btn-emphasize {
    @apply text-white bg-gradient-to-r from-red to-orange;
  }

  a.btn-emphasize:hover,
  a.btn-emphasize:focus,
  button.btn-emphasize:hover,
  button.btn-emphasize:focus {
    background-image: linear-gradient(to right, rgba(226, 40, 40, 0.6), rgba(255, 140, 47, 0.6));
  }

  .btn-error {
    @apply text-red bg-transparent;
  }
}

.button {
  @apply text-orange;
  @apply font-semibold;
  @apply leading-none;
  @apply uppercase;
  @apply tracking-wide;
  @apply text-center;
  @apply no-underline;
  @apply inline-block;
  @apply px-10;
  @apply py-4;
  @apply bg-transparent;
  @apply rounded-sm;

  background:
    /* top */
    linear-gradient(
      to right,
      #e03237 0%,
      #fd8c3c 100%
    )
    left
    top
    no-repeat,
    /* bottom */
    linear-gradient(
      to right,
      #e03237 0%,
      #fd8c3c 100%
    )
    left
    bottom
    no-repeat,
    /* right */
    linear-gradient(
      to top,
      #e03237 0%,
      #e03237 100%
    )
    left
    bottom
    no-repeat,
    /* right */
    linear-gradient(
      to top,
      #fd8c3c 0%,
      #fd8c3c 100%
    )
    right
    bottom
    no-repeat;
  background-size: 100% 2px, 100% 2px, 2px 100%, 2px 100%;
  border: 0;
  transition: none;

  &:active {
    transform: scale(.975);
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
  }

  &:hover,
  &:focus {
    @apply text-white;
    background: linear-gradient(to right, theme('colors.red') 0%, theme('colors.orange') 100%);
    box-shadow: 0;
  }
}

.button-alt {
  @apply text-white;
  @apply font-semibold;
  @apply leading-none;
  @apply uppercase;
  @apply tracking-wide;
  @apply text-center;
  @apply no-underline;
  @apply inline-block;
  @apply rounded-sm;

  background: linear-gradient(to right, theme('colors.red') 0%, theme('colors.orange') 100%);
  transition: none;

  &:active {
    transform: scale(.975);
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
  }

  &:hover,
  &:focus {
    @apply bg-transparent;
    @apply text-orange;
    background:
      /* top */
      linear-gradient(
        to right,
        #e03237 0%,
        #fd8c3c 100%
      )
      left
      top
      no-repeat,
      /* bottom */
      linear-gradient(
        to right,
        #e03237 0%,
        #fd8c3c 100%
      )
      left
      bottom
      no-repeat,
      /* right */
      linear-gradient(
        to top,
        #e03237 0%,
        #e03237 100%
      )
      left
      bottom
      no-repeat,
      /* right */
      linear-gradient(
        to top,
        #fd8c3c 0%,
        #fd8c3c 100%
      )
      right
      bottom
      no-repeat;
    background-size: 100% 2px, 100% 2px, 2px 100%, 2px 100%;
    border: 0;
    box-shadow: 0;
  }
}

/*! purgecss start ignore */
.fade-enter-active, .fade-leave-active {
  transition: opacity .15s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slideVerticalFade-enter-active, .slideVerticalFade-leave-active {
  transition: opacity .15s ease-out, transform .15s ease-out;
}

.slideVerticalFade-enter, .slideVerticalFade-leave-to {
  opacity: 0;
  transform: translateY(36px);
}

.slideHorizontalFade-enter-active, .slideHorizontalFade-leave-active {
  transition: opacity 1s ease-out, transform 1s ease-out;
}

.slideHorizontalFade-enter, .slideHorizontalFade-leave-to {
  opacity: 0;
  transform: translateX(36px);
}

.blur {
  filter: blur(3px);
}

.UnraidUPC-serverError {
  a.btn,
  button {
    @apply text-center text-white text-14px underline;

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
}

.button-pill {
  @apply text-white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  background: linear-gradient(to right, theme('colors.red') 0%, theme('colors.orange') 100%);
  box-shadow: 0;
  border: 0;
  border-radius: .125rem;

  &:hover,
  &:focus {
    @apply bg-transparent;
    @apply text-orange;

    background:
      /* top */
      linear-gradient(
        to right,
        #e03237 0%,
        #fd8c3c 100%
      )
      left
      top
      no-repeat,
      /* bottom */
      linear-gradient(
        to right,
        #e03237 0%,
        #fd8c3c 100%
      )
      left
      bottom
      no-repeat,
      /* right */
      linear-gradient(
        to top,
        #e03237 0%,
        #e03237 100%
      )
      left
      bottom
      no-repeat,
      /* right */
      linear-gradient(
        to top,
        #fd8c3c 0%,
        #fd8c3c 100%
      )
      right
      bottom
      no-repeat;
    background-size: 100% 1px, 100% 1px, 1px 100%, 1px 100%;
    border: 0;
    border-radius: .125rem;
    box-shadow: 0;
  }
}
/*! purgecss end ignore */
</style>
